import React, { useCallback, useEffect, useRef, useState } from 'react';
import Grid from '@material-ui/core/Grid';
import Divider from '@material-ui/core/Divider';
import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';
import { RiInputMethodLine } from 'react-icons/ri';
import { FiLock } from 'react-icons/fi';
import { MdWork } from 'react-icons/md';
import * as Yup from 'yup';

import api from '../../../../services/api';
import getValidationErrors from '../../../../utils/getValidationErrors';
import { useAuth } from '../../../../hooks/auth';
import { useToast } from '../../../../hooks/toast';
import styleMaterial from './styles/styleMaterial';
import InputLogged from '../../../../components/InputLogged';
import {
  Wrapper,
  PageTitle,
  InputWrapper,
  ButtonStyled,
  InformativeSpan,
  PasswordActions,
  PasswordFormActions,
  PasswordSummary,
} from './styles';

interface UpdateUserRequest {
  nome: string;
  cargo: string;
  senha_atual: string;
  nova_senha: string;
  confirmacao_senha: string;
}

interface PdvAdministrativePasswordMetadata {
  enabled: boolean;
  version: number;
  propagationStatus: string;
  passwordDefinedAt: string | null;
  lastEnabledAt: string | null;
  updatedAt: string | null;
  disabledAt: string | null;
  lastDisabledAt: string | null;
}

interface PdvAdministrativePasswordFormData {
  password: string;
  confirmation: string;
}

export const PdvAdministrativePasswordCard: React.FC<{
  companyId: number;
}> = ({ companyId }) => {
  const { addToast } = useToast();
  const [metadata, setMetadata] = useState<PdvAdministrativePasswordMetadata>();
  const [metadataLoading, setMetadataLoading] = useState(true);
  const [metadataLoadFailed, setMetadataLoadFailed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const formRef = useRef<FormHandles>(null);

  const formatDateTime = (value: string | null) => {
    if (!value) return 'não registrada';

    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(value));
  };

  const loadMetadata = useCallback(async () => {
    setMetadataLoading(true);
    setMetadataLoadFailed(false);
    try {
      const response = await api.get(
        `/companies/${companyId}/pdv-administrative-password`,
      );
      setMetadata(response.data);
    } catch {
      setMetadataLoadFailed(true);
      addToast({
        type: 'error',
        title: 'Não foi possível consultar a senha do PDV',
      });
    } finally {
      setMetadataLoading(false);
    }
  }, [addToast, companyId]);

  useEffect(() => {
    loadMetadata();
  }, [loadMetadata]);

  const handleDefine = async ({
    password,
    confirmation,
  }: PdvAdministrativePasswordFormData) => {
    if (password.length < 8 || password !== confirmation) {
      addToast({
        type: 'error',
        title: 'Senha inválida',
        description: 'Use ao menos 8 caracteres e confirme a mesma senha.',
      });
      return;
    }

    setLoading(true);
    try {
      const response = await api.put(
        `/companies/${companyId}/pdv-administrative-password`,
        { password },
      );
      setMetadata(response.data);
      formRef.current?.reset();
      setIsEditing(false);
      addToast({
        type: 'success',
        title: 'Senha administrativa do PDV atualizada',
        description: 'A propagação para os terminais foi iniciada.',
      });
    } catch {
      addToast({
        type: 'error',
        title: 'Não foi possível atualizar a senha administrativa',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = async () => {
    setLoading(true);
    try {
      const response = await api.delete(
        `/companies/${companyId}/pdv-administrative-password`,
      );
      setMetadata(response.data);
      formRef.current?.reset();
      setIsEditing(false);
      addToast({
        type: 'success',
        title: 'Senha administrativa desativada',
      });
    } catch {
      addToast({
        type: 'error',
        title: 'Não foi possível desativar a senha administrativa',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Grid item xs={12}>
      <Divider />
      <PageTitle>Senha administrativa do PDV</PageTitle>
      <InformativeSpan>
        Protege configurações e ações administrativas nos terminais. A senha
        não é exibida nem armazenada em texto puro.
      </InformativeSpan>
      <Grid container spacing={3}>
        {metadataLoading ? (
          <Grid item xs={12}>
            <InformativeSpan role="status">
              Carregando senha administrativa do PDV...
            </InformativeSpan>
          </Grid>
        ) : metadataLoadFailed || !metadata ? (
          <Grid item xs={12}>
            <InformativeSpan>
              Não foi possível carregar a senha administrativa do PDV.
            </InformativeSpan>
          </Grid>
        ) : metadata.enabled && !isEditing ? (
          <>
            <Grid item xs={12}>
              <PasswordSummary>
                <strong>
                  Senha definida em {formatDateTime(metadata.passwordDefinedAt)}
                </strong>
              </PasswordSummary>
            </Grid>
            <Grid item xs={12}>
              <PasswordActions>
                <ButtonStyled
                  type="button"
                  disabled={loading}
                  onClick={() => setIsEditing(true)}
                >
                  Definir nova senha
                </ButtonStyled>
                <ButtonStyled
                  type="button"
                  disabled={loading}
                  onClick={handleDisable}
                >
                  Desativar
                </ButtonStyled>
              </PasswordActions>
            </Grid>
          </>
        ) : (
          <Grid item xs={12}>
            <Form ref={formRef} onSubmit={handleDefine}>
              <Grid container spacing={3}>
                <Grid item lg={4} xs={12}>
                  <InputWrapper>
                    <label htmlFor="pdv-administrative-password">
                      Nova senha:
                    </label>
                    <InputLogged
                      id="pdv-administrative-password"
                      name="password"
                      type="password"
                      placeholder="Digite a nova senha"
                      icon={FiLock}
                      autoComplete="new-password"
                    />
                  </InputWrapper>
                </Grid>
                <Grid item lg={4} xs={12}>
                  <InputWrapper>
                    <label htmlFor="pdv-administrative-password-confirmation">
                      Confirmação:
                    </label>
                    <InputLogged
                      id="pdv-administrative-password-confirmation"
                      name="confirmation"
                      type="password"
                      placeholder="Repita a nova senha"
                      icon={FiLock}
                      autoComplete="new-password"
                    />
                  </InputWrapper>
                </Grid>
                <Grid item xs={12}>
                  <Grid container justify="center">
                    <Grid item lg={4} md={6} xs={12}>
                      <PasswordFormActions data-testid="pdv-administrative-password-form-actions">
                        <ButtonStyled type="submit" disabled={loading}>
                          {metadata?.enabled
                            ? 'Salvar nova senha'
                            : 'Definir senha'}
                        </ButtonStyled>
                        {metadata?.enabled && (
                          <ButtonStyled
                            type="button"
                            disabled={loading}
                            onClick={() => {
                              formRef.current?.reset();
                              setIsEditing(false);
                            }}
                          >
                            Cancelar
                          </ButtonStyled>
                        )}
                      </PasswordFormActions>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <InformativeSpan>
                    Status: {metadata?.enabled ? 'ativa' : 'desativada'} · versão{' '}
                    {metadata?.version ?? 0} · propagação{' '}
                    {metadata?.propagationStatus ?? 'não configurada'}
                  </InformativeSpan>
                </Grid>
              </Grid>
            </Form>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

const Usuario: React.FC = () => {
  const formRef = useRef<FormHandles>(null);

  const { user, updateUser } = useAuth();
  const canManagePdvAdministrativePassword = [user.cargo, user.acesso]
    .map((value) =>
      String(value ?? '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .toLowerCase(),
    )
    .some((role) => role === 'administrador' || role === 'admin');
  const { addToast } = useToast();

  const classes = styleMaterial();

  const handleUpdateUser = useCallback(
    async (data: UpdateUserRequest) => {
      try {
        formRef.current?.setErrors({});

        const schema = Yup.object().shape({
          senha_atual: Yup.string(),
          nova_senha: Yup.string().when('senha_atual', {
            is: (val) => !!val.length,
            then: Yup.string().required('Campo obrigatório'),
            otherwise: Yup.string(),
          }),
          confirmacao_senha: Yup.string().when('senha_atual', {
            is: (val) => !!val.length,
            then: Yup.string().required('Campo obrigatório'),
            otherwise: Yup.string(),
          }),
        });

        await schema.validate(data, { abortEarly: false });

        const { nome, cargo, senha_atual, nova_senha } = data;

        const formData = {
          nome,
          cargo,
          ...(senha_atual
            ? {
                senha_atual,
                nova_senha,
              }
            : {}),
        };

        api
          .put(`usuario/profile/${user.id}`, formData)
          .then((response) => {
            addToast({
              type: 'success',
              title: 'Dados atualizados',
              description: 'Seus dados foram atualizado com sucesso',
            });

            updateUser(response.data);
          })
          .catch(() => {
            addToast({
              type: 'error',
              title: 'Erro durante a atualização',
            });
          });
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);

          formRef.current?.setErrors(errors);
        }

        addToast({
          type: 'error',
          title: 'Erro durante a atualização',
          description: 'Ocorreu um erro ao atualizar seu usuário.',
        });
      }
    },
    [addToast, user.id, updateUser],
  );

  return (
    <Wrapper>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <PageTitle>Edite seus dados pessoais</PageTitle>
        </Grid>

        <Grid item xs={12}>
          <Form
            ref={formRef}
            onSubmit={handleUpdateUser}
            className={classes.root}
            initialData={{
              nome: user.nome,
              cargo: user.cargo,
            }}
          >
            <Grid container spacing={3}>
              <Grid item lg={7} md={8} xs={12}>
                <InputWrapper>
                  <span>Nome:</span>

                  <InputLogged
                    name="nome"
                    placeholder="Digite o seu nome"
                    icon={RiInputMethodLine}
                  />
                </InputWrapper>
              </Grid>

              <Grid item lg={5} md={4} xs={12}>
                <InputWrapper>
                  <span>Cargo:</span>

                  <InputLogged
                    name="cargo"
                    placeholder="Digite o seu cargo"
                    icon={MdWork}
                  />
                </InputWrapper>
              </Grid>

              <Grid item xs={12}>
                <Divider />
                <InformativeSpan>
                  Caso queira alterar sua senha, preencha os campos abaixo.
                </InformativeSpan>
              </Grid>

              <Grid item lg={4} xs={12}>
                <InputWrapper>
                  <span>Senha:</span>

                  <InputLogged
                    type="password"
                    name="senha_atual"
                    placeholder="Sua senha atual"
                    icon={FiLock}
                  />
                </InputWrapper>
              </Grid>

              <Grid item lg={4} xs={12}>
                <InputWrapper>
                  <span>Nova senha:</span>

                  <InputLogged
                    type="password"
                    name="nova_senha"
                    placeholder="Sua nova senha"
                    icon={FiLock}
                  />
                </InputWrapper>
              </Grid>

              <Grid item lg={4} xs={12}>
                <InputWrapper>
                  <span>Confirme sua senha:</span>

                  <InputLogged
                    type="password"
                    name="confirmacao_senha"
                    placeholder="Repita sua nova senha"
                    icon={FiLock}
                  />
                </InputWrapper>
              </Grid>

              <Grid item xs={12}>
                <Grid container justify="center">
                  <Grid item lg={4} md={6} xs={12}>
                    <ButtonStyled type="submit">Salvar</ButtonStyled>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Form>
        </Grid>
      </Grid>
      {canManagePdvAdministrativePassword && (
        <Grid container spacing={3}>
          <PdvAdministrativePasswordCard companyId={user.empresa_id} />
        </Grid>
      )}
    </Wrapper>
  );
};

export default Usuario;
